import React from "react";
import { Linkedin, Youtube, Instagram, Mail, Calendar } from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import { LINKS } from "@/lib/links";
import { NewsletterForm } from "@/components/site/Newsletter";
import { Logo } from "@/components/site/Logo";

export const Footer = () => {
  return (
    <footer
      data-testid={TEST_IDS.footer.section}
      className="bg-nearblack text-paper/80 pt-24 md:pt-28 pb-12"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Footer newsletter block */}
        <div id="newsletter" className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-16 border-b border-paper/10 scroll-mt-28">
          <div className="lg:col-span-7">
            <p className="eyebrow-light mb-6">Stay Close</p>
            <h2
              className="font-serif text-paper leading-[1.08] text-balance"
              style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.85rem)", fontWeight: 400 }}
            >
              Join the letter.
              <br />
              <em className="italic text-camellight">
                Strategic banking. Infinite possibilities.
              </em>
            </h2>
            <p className="mt-5 max-w-lg text-paper/65 text-[14.5px] leading-[1.85] font-light">
              A quiet, thoughtful note arrives in your inbox — financial
              literacy, cash flow, Infinite Banking, books, and the
              long-term work of building wealth.
            </p>
          </div>
          <div className="lg:col-span-5 self-end">
            <div data-testid={TEST_IDS.newsletter.footer}>
              <NewsletterForm variant="inline" />
            </div>
          </div>
        </div>

        {/* Footer columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pt-16">
          <div className="lg:col-span-5">
            <Logo light withTagline />
            <p className="mt-7 max-w-md text-paper/60 text-[14px] font-light leading-[1.85]">
              Financial systems education for high-income earners, business
              owners, and growing families who want more control, clarity,
              and flexibility with their money.
            </p>
            <a
              href={LINKS.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TEST_IDS.footer.calendly}
              className="mt-8 btn-outline-light inline-flex items-center gap-2"
            >
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              Work with Liz
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow-light mb-5">Explore</p>
            <ul className="space-y-3 text-[14px]">
              <li><a href="#approach" className="hover:text-camellight transition-colors">Approach</a></li>
              <li><a href="#about" className="hover:text-camellight transition-colors">About Liz</a></li>
              <li><a href="#newsletter" className="hover:text-camellight transition-colors">The Letter</a></li>
              <li><a href="#policy-review" className="hover:text-camellight transition-colors">Policy Review</a></li>
              <li><a href="#learning-credibility" className="hover:text-camellight transition-colors">Ongoing Learning</a></li>
              <li><a href="#contact" className="hover:text-camellight transition-colors">Book a Call</a></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="eyebrow-light mb-5">Connect</p>
            <ul className="space-y-3 text-[14px]">
              <li>
                <a
                  href={LINKS.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={TEST_IDS.footer.linkedin}
                  className="inline-flex items-center gap-2 hover:text-camellight transition-colors"
                >
                  <Linkedin className="w-4 h-4" strokeWidth={1.5} />
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href={LINKS.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={TEST_IDS.footer.youtube}
                  className="inline-flex items-center gap-2 hover:text-camellight transition-colors"
                >
                  <Youtube className="w-4 h-4" strokeWidth={1.5} />
                  YouTube
                </a>
              </li>
              <li>
                <a
                  href={LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={TEST_IDS.footer.instagram}
                  className="inline-flex items-center gap-2 hover:text-camellight transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href={LINKS.email}
                  data-testid={TEST_IDS.footer.email}
                  className="inline-flex items-center gap-2 hover:text-camellight transition-colors"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                  liz@lizdirocco.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-paper/10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <p
            className="text-paper/45"
            style={{ fontSize: "0.62rem", letterSpacing: "0.32em", textTransform: "uppercase", fontWeight: 500 }}
          >
            © {new Date().getFullYear()} Liz Dirocco · lizdirocco.com
          </p>
          <p className="text-[11px] text-paper/45 max-w-2xl leading-relaxed font-light">
            Educational content only. Not financial, tax, legal, or
            investment advice. Individual results and suitability depend on
            personal circumstances.
          </p>
        </div>
      </div>
    </footer>
  );
};
